import { Injectable } from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {forkJoin, Observable, of} from 'rxjs';
import {API_LIST} from '@core/services/web-api/api-list';
import {catchError, map, mergeMap, share, take, tap} from 'rxjs/operators';
import {PageRaffleList, RaffleCategory, RaffleCategoryMetadata, RaffleCategoryMetadataIndexedList} from '@core/models/raffle.model';
import {PathTypeToken, PathType} from './root-path-checker.models';

@Injectable({
  providedIn: 'root'
})
export class RootPathCheckerService {
  categories$: Observable<RaffleCategory[]>;
  categoriesIndexed: RaffleCategoryMetadataIndexedList = {};

  constructor(private httpClient: HttpClient) {
    this.categories$ = this.httpClient.get(API_LIST.getCategories()).pipe(
      tap((response: RaffleCategory[]) => {
        let metadataIndexedList: RaffleCategoryMetadataIndexedList = {};

        let addMetadata: RaffleCategoryMetadata[] = this.getRaffleCategoryMetadataList();

        for (let newMetadata of addMetadata) {
          metadataIndexedList[newMetadata.slug] = newMetadata;
        }

        response.forEach((category: RaffleCategory) => {
          let currentMetadata = new RaffleCategoryMetadata();
          currentMetadata.name = category.name;
          currentMetadata.slug = category.slug;
          currentMetadata.sortBy = category.sort_by ? category.sort_by : null;
          metadataIndexedList[category.slug] = currentMetadata;
        });

        this.categoriesIndexed = metadataIndexedList;
      }),
      share()
    );
  }

  getRaffleCategoryMetadataList(): RaffleCategoryMetadata[] {
    return [
      this.getCompetitionsMetadata('All Competitions', 'all-competitions', null, 'gray', true, true, true),
    ];
  }

  getCompetitionsMetadata(title, slug, filter, theme, containerFluid = false, showAllRaffles = true, showSorting = false, sortBy = null) {
    let metadata = new RaffleCategoryMetadata();
    metadata.name = title;
    metadata.slug = slug;
    metadata.containerFluid = containerFluid;
    metadata.showAllRaffles = showAllRaffles;
    metadata.showSorting = showSorting;
    metadata.theme = theme;
    metadata.filter = filter;
    metadata.sortBy = sortBy;

    return metadata;
  }

  checkByUrl(url: string): Observable<PathTypeToken> {
    const currentUrlSegments = url.split('/');

    if (currentUrlSegments.length === 2) {
      const slug = currentUrlSegments[1];

      return this.checkBySlug(slug);
    }

    const token = new PathTypeToken();
    token.slug = null;
    token.pathType = PathType.none;

    return of(token);
  }

  pageRaffle(slug: string) {
    return this.httpClient.get(API_LIST.getPageRaffle(slug, 'GBP')).pipe(map((page: PageRaffleList) => {
      const token = new PathTypeToken();
      token.slug = slug;
      token.pathType = PathType.category;

      const categoryMeta = new RaffleCategoryMetadata();
      categoryMeta.slug = page.page_data.slug;
      categoryMeta.name = page.page_data.title;
      categoryMeta.theme = page.page_data.theme;
      categoryMeta.filter = page.page_data.raffle_draws_at.substr(0, 10);
      categoryMeta.showAllRaffles = true;

      token.categoryData = categoryMeta;

      return token;
    }), catchError(() => {
      return of(null);
    }));
  }

  checkBySlug(slug: string): Observable<PathTypeToken> {
    const category$ = this.categories$.pipe(mergeMap(() => {
      if (!this.categoriesIndexed[slug]) {
        return of(null);
      }

      const token = new PathTypeToken();
      token.slug = slug;
      token.pathType = PathType.category;
      token.categoryData = this.categoriesIndexed[slug];

      return of(token);
    }));

    return forkJoin({
      category: category$,
      page: this.pageRaffle(slug)
    }).pipe(map((race: any) => {
      if (race.page) {
        return race.page;
      }

      if (race.category) {
        return race.category;
      }

      return this.checkWinnerAndResultBySlug(slug);
    }));
  }

  checkWinnerAndResultBySlug(slug: string): Observable<PathTypeToken> {
    return forkJoin([
      this.httpClient.get(API_LIST.getWinner(slug)).pipe(catchError(() => of(null))),
      this.httpClient.get(API_LIST.getDrawResult(slug)).pipe(catchError(() => of(null)))
    ]).pipe(
      take(1),
      map((apiCheckResult) => {
        const token = new PathTypeToken();
        token.slug = slug;

        if (apiCheckResult[0]) {
          token.pathType = PathType.winner;
        } else if (apiCheckResult[1]) {
          token.pathType = PathType.result;
        } else {
          token.pathType = PathType.none;
        }

        return token;
      })
    );
  }
}
